import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ShoppingBag, UserPlus, Music2, Star, FileSignature, Mail, CheckCheck, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { NotifReply } from "../components/NotifReply";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

const KIND_META = {
  sale: { icon: ShoppingBag, label: "Vente" },
  artist: { icon: UserPlus, label: "Artiste" },
  client: { icon: UserPlus, label: "Client" },
  track: { icon: Music2, label: "Morceau" },
  review: { icon: Star, label: "Avis" },
  contract: { icon: FileSignature, label: "Contrat" },
  message: { icon: Mail, label: "Message" },
  contact: { icon: Mail, label: "Contact" },
  general: { icon: Bell, label: "Info" },
};

const relTime = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const StudioNotifications = () => {
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    api.get("/admin/notifications")
      .then((r) => {
        setItems(r.data.notifications || []);
        setUnread(r.data.unread || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (checked && (!user || user.role !== "admin")) navigate("/", { replace: true });
  }, [checked, user, navigate]);

  // Pas de rendu tant que le rôle n'est pas confirmé (évite tout flash de contenu admin)
  if (!checked || !user || user.role !== "admin") return null;

  const markAll = async () => {
    await api.post("/admin/notifications/read").catch(() => {});
    setUnread(0);
    setItems((arr) => arr.map((n) => ({ ...n, read: true })));
  };

  const removeNotif = async (n) => {
    try {
      await api.delete(`/admin/notifications/${n.notif_id}`);
      setItems((arr) => arr.filter((x) => x.notif_id !== n.notif_id));
      if (!n.read) setUnread((u) => Math.max(0, u - 1));
    } catch {
      /* silencieux */
    }
  };

  return (
    <main data-testid="notifications-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Studio</p>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-5xl">
              Notifications
            </h1>
          </div>
          {unread > 0 && (
            <button
              onClick={markAll}
              data-testid="notif-mark-all"
              className="flex items-center gap-2 border border-amber-500/50 px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
            >
              <CheckCheck className="h-4 w-4" /> Tout marquer lu ({unread})
            </button>
          )}
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <section className="mt-10 border border-white/[0.08] bg-surface" data-testid="notif-list">
          {items.length === 0 ? (
            <p className="p-10 text-center text-sm text-zinc-600">Aucune notification pour le moment.</p>
          ) : (
            items.map((n) => {
              const meta = KIND_META[n.kind] || KIND_META.general;
              const Icon = meta.icon;
              return (
                <div
                  key={n.notif_id}
                  data-testid={`notif-item-${n.notif_id}`}
                  className={`flex gap-4 border-b border-white/[0.05] px-6 py-5 ${n.read ? "opacity-55" : ""}`}
                >
                  <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center border ${n.read ? "border-white/10 text-zinc-500" : "border-amber-500/40 bg-amber-500/10 text-amber-400"}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-medium text-zinc-100">{n.title}</p>
                      <span className="flex items-center gap-2 text-[10px] text-zinc-600">
                        {relTime(n.created_at)}
                        <button
                          onClick={() => removeNotif(n)}
                          data-testid={`notif-delete-${n.notif_id}`}
                          title="Supprimer cette notification"
                          className="text-zinc-600 transition-colors hover:text-red-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                    <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-zinc-600">{meta.label}</p>
                    {(n.lines || []).map((l, i) => (
                      <p key={i} className="mt-1 text-sm leading-relaxed text-zinc-400">{l}</p>
                    ))}
                    <NotifReply notif={n} />
                  </div>
                </div>
              );
            })
          )}
        </section>
      </Reveal>

      <div className="mt-10" />
    </main>
  );
};

export default StudioNotifications;
